import { z } from 'zod'

/**
 * Plain calendar dates — `YYYY-MM-DD`, with no time and no zone.
 *
 * `docs/ARCHITECTURE.md` §4: "Timestamps are UTC ISO-8601. Calendar dates are plain
 * `YYYY-MM-DD` and are interpreted in the **tenant's** timezone." This module owns the
 * first half of that sentence — the value itself and arithmetic on it — and knows nothing
 * about time zones. `./tenant-day.ts` owns the second half.
 *
 * The type is branded so that a `PlainDate` cannot be confused with an arbitrary string,
 * and the format is fixed-width so that **lexicographic order is chronological order**.
 * That is not a coincidence to be relied on silently: it is why the expiry sweep can ask
 * `date < today` in SQL, and why `comparePlainDates` is a string comparison.
 *
 * `Date` is used inside this module purely as a proleptic Gregorian calculator — always
 * through its UTC accessors, never its local ones. UTC has no DST, so the arithmetic is
 * exact; no ambient timezone can influence any result here.
 */

/** The wire and storage format. Four-digit years only, which keeps sorting total. */
export const PLAIN_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const MILLISECONDS_PER_DAY = 86_400_000

/** A calendar date decomposed into its fields. `month` is 1–12, not 0–11. */
export type CivilDateParts = {
  readonly year: number
  readonly month: number
  readonly day: number
}

/** Splits a well-formed `YYYY-MM-DD` string. Assumes the shape; does not validate it. */
function partsOfWellFormed(value: string): CivilDateParts {
  return {
    year: Number(value.slice(0, 4)),
    month: Number(value.slice(5, 7)),
    day: Number(value.slice(8, 10)),
  }
}

/**
 * The UTC instant of midnight on a civil date, used as a calendar calculation only.
 *
 * `Date.UTC` maps years 0–99 into the 20th century, so the year is set explicitly through
 * `setUTCFullYear` instead.
 */
function civilMidnightUtcMs(parts: CivilDateParts): number {
  const date = new Date(0)
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day)
  date.setUTCHours(0, 0, 0, 0)
  return date.getTime()
}

/**
 * Whether the fields name a date that exists.
 *
 * `2025-02-30` matches the pattern and is not a date; JavaScript would silently roll it
 * forward to 2 March, which is exactly the class of quiet wrongness this package exists
 * to prevent.
 */
export function isRealCalendarDate(parts: CivilDateParts): boolean {
  if (
    !Number.isInteger(parts.year) ||
    !Number.isInteger(parts.month) ||
    !Number.isInteger(parts.day)
  ) {
    return false
  }
  const rolled = new Date(civilMidnightUtcMs(parts))
  return (
    rolled.getUTCFullYear() === parts.year &&
    rolled.getUTCMonth() === parts.month - 1 &&
    rolled.getUTCDate() === parts.day
  )
}

/** Whether a value is a well-formed calendar date that actually exists. */
export function isPlainDate(value: unknown): value is PlainDate {
  return (
    typeof value === 'string' &&
    PLAIN_DATE_PATTERN.test(value) &&
    isRealCalendarDate(partsOfWellFormed(value))
  )
}

/**
 * A calendar date in the tenant's timezone: `YYYY-MM-DD`, no time, no offset.
 */
export const plainDateSchema = z
  .string()
  .regex(PLAIN_DATE_PATTERN, { error: 'Expected a calendar date formatted YYYY-MM-DD.' })
  .refine((value) => isRealCalendarDate(partsOfWellFormed(value)), {
    error: 'Expected a date that exists in the calendar.',
  })
  .meta({
    id: 'PlainDate',
    format: 'date',
    description: "A calendar date, YYYY-MM-DD, interpreted in the tenant's configured timezone.",
  })
  .brand<'PlainDate'>()

export type PlainDate = z.infer<typeof plainDateSchema>

/**
 * Builds a `PlainDate` from its fields.
 *
 * @throws if the fields do not name a real date.
 */
export function plainDateFromParts(parts: CivilDateParts): PlainDate {
  if (!isRealCalendarDate(parts)) {
    throw new RangeError(
      `Not a real calendar date: year ${parts.year}, month ${parts.month}, day ${parts.day}.`,
    )
  }
  const year = String(parts.year).padStart(4, '0')
  const month = String(parts.month).padStart(2, '0')
  const day = String(parts.day).padStart(2, '0')
  return `${year}-${month}-${day}` as PlainDate
}

/** Decomposes a `PlainDate` into its fields. */
export function plainDateParts(date: PlainDate): CivilDateParts {
  return partsOfWellFormed(date)
}

/**
 * The date `days` calendar days after `date` (negative counts backwards).
 *
 * Calendar arithmetic, not elapsed time: adding one day to the date a DST transition
 * falls on still yields the next date, because a calendar day is a label rather than a
 * duration. The tenant-local instant those labels map to is `./tenant-day.ts`'s problem.
 */
export function addPlainDays(date: PlainDate, days: number): PlainDate {
  if (!Number.isInteger(days)) {
    throw new RangeError(`Cannot add a fractional number of days: ${days}.`)
  }
  const shifted = new Date(civilMidnightUtcMs(plainDateParts(date)) + days * MILLISECONDS_PER_DAY)
  return plainDateFromParts({
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  })
}

/** `-1`, `0` or `1` — chronological order, which for this format is string order. */
export function comparePlainDates(left: PlainDate, right: PlainDate): -1 | 0 | 1 {
  if (left < right) {
    return -1
  }
  if (left > right) {
    return 1
  }
  return 0
}
